import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './nocturne.css';
import './Landing.css';

const STEPS = [
  { num: '01', title: 'Your bank alerts you', copy: 'PocketGuru reads transaction SMS — HSBC today, more banks rolling out. Gmail alerts for banks like Axis are planned.' },
  { num: '02', title: 'We parse the mess', copy: 'Amount, account, balance and the raw UPI merchant string are pulled out of the message. No bank login, no passwords.' },
  { num: '03', title: 'You name it once in Telegram', copy: 'The transaction arrives with one-tap category buttons. UPI strings like BHARATPE*7X2K9 finally get a human name.' },
  { num: '04', title: 'It remembers, and reports', copy: 'That merchant auto-categorizes from then on. Ask for stats in Telegram or open the web dashboard — both stay current.' }
];

const CHIPS = ['Food & drink', 'Groceries', 'Travel', 'Something else'];

const FEATURES = [
  { title: 'UPI merchant memory', copy: 'The core idea: categorize a cryptic UPI merchant once and every future payment to it files itself.' },
  { title: 'Stats in Telegram', copy: 'Ask for this week, this month or a category right in the chat — no app switch, no dashboard needed.' },
  { title: 'Web dashboard', copy: 'Balances, expenses, category and merchant analytics, plus a searchable archive of every alert.' },
  { title: 'All accounts, one view', copy: 'Multiple banks and cards read from the alerts they already send, totalled on one screen.' },
  { title: 'Passwordless login', copy: 'Username plus a one-time code delivered over Telegram. Nothing to remember, nothing to leak.' },
  { title: 'Built for Indian money', copy: 'UPI-first parsing, rupee formatting, and the merchant chaos that comes with both.' }
];

const ROADMAP = [
  { when: 'Live now', title: 'Expense clarity', copy: 'SMS parsing, Telegram categorization, merchant memory, balances and analytics.' },
  { when: 'Next', title: 'Goals & savings', copy: "Set a target, see what this month's spending does to it, and get nudged before payday disappears." },
  { when: 'Planned', title: 'Investments & trading', copy: 'Portfolio tracking alongside your spending, then research and trading tools in the same place.' },
  { when: 'Planned', title: 'Learn as you go', copy: 'Short, practical lessons on managing money, saving and investing — written for first-time earners.' }
];

const TRUST = [
  'No bank passwords, ever — PocketGuru only reads the alerts your bank already sends you.',
  'Log in with your username and a one-time code over Telegram.',
  'You decide every category. Nothing is guessed until you have confirmed it once.',
  'Disconnect Telegram or Gmail whenever you like (Gmail connection planned).'
];

export default function Landing() {
  useEffect(() => {
    document.body.classList.add('pg-theme-nocturne');
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced || !('IntersectionObserver' in window)) {
      document.querySelectorAll('.pg-reveal').forEach((el) => el.classList.add('pg-in'));
      return () => document.body.classList.remove('pg-theme-nocturne');
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.classList.add('pg-in');
            io.unobserve(en.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );
    document.querySelectorAll('.pg-reveal').forEach((el) => io.observe(el));
    return () => {
      io.disconnect();
      document.body.classList.remove('pg-theme-nocturne');
    };
  }, []);

  return (
    <div className="pg-landing">
      <nav className="nav pg-nav">
        <a href="#top" className="nav-brand pg-brand">Pocket<span>Guru</span></a>
        <span className="pg-navlinks">
          <a href="#how">How it works</a>
          <a href="#features">Features</a>
          <a href="#roadmap">Roadmap</a>
          <a href="#privacy">Privacy</a>
        </span>
        <Link to="/login" className="btn btn-secondary pg-cta">Log in</Link>
        <Link to="/register" className="btn btn-primary pg-cta">Register</Link>
      </nav>

      <section id="top" className="pg-hero">
        <div className="pg-rise pg-hero-copy">
          <p className="pg-wordmark">Pocket<span>Guru</span></p>
          <h1>
            <span className="pg-block">Every UPI spend, sorted.</span>
            <span className="pg-block pg-accent">Every account, in one place.</span>
          </h1>
          <p className="pg-lead">
            UPI alerts name a payment gateway, not the shop. Tell PocketGuru once what it was — every future payment to that merchant sorts itself, across every bank account.
          </p>
          <div className="pg-cta-row">
            <Link to="/login" className="btn btn-primary pg-cta pg-cta-lg">Get started</Link>
            <a href="#how" className="btn btn-secondary pg-cta pg-cta-lg">See how it works</a>
          </div>
        </div>
        <div className="pg-hero-visual">
          <div className="lighten pg-hero-plane">
            <img
              src="/assets/hero-phone-dark.png"
              alt="PocketGuru expense dashboard on a phone with bank SMS alerts"
              className="pg-hero-img"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
            <div className="pg-hero-fallback" aria-hidden="true" />
          </div>
          <div className="pg-hero-fade" />
        </div>
      </section>

      <section id="how" className="pg-section">
        <span className="pg-reveal pg-kicker">How it works</span>
        <h2 className="pg-reveal pg-h2">Four taps in the first week. Then it runs itself.</h2>
        <div className="pg-how-grid">
          <div>
            {STEPS.map((step) => (
              <div key={step.num} className="pg-reveal pg-step">
                <p className="pg-step-num">{step.num}</p>
                <div>
                  <h3 className="pg-h3">{step.title}</h3>
                  <p className="pg-muted">{step.copy}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="pg-reveal pg-telegram-mock">
            <span className="pg-mock-label">PocketGuru · Telegram</span>
            <div className="pg-txn-tile">
              <p className="pg-txn-amount">₹248.00</p>
              <p className="pg-muted-sm">UPI/DR/5391··/BHARATPE*7X2K9 — HSBC ··4471<br />Balance ₹32,905.14</p>
              <p className="pg-accent-sm">Which category?</p>
            </div>
            <div className="pg-chips">
              {CHIPS.map((c) => (
                <span key={c} className="tag tag-outline pg-chip">{c}</span>
              ))}
            </div>
            <p className="pg-hint">Tap once. Next BHARATPE*7X2K9 payment files itself under the same category.</p>
          </div>
        </div>
      </section>

      <hr className="pg-rule" />

      <section id="features" className="pg-section">
        <span className="pg-reveal pg-kicker">Features</span>
        <h2 className="pg-reveal pg-h2 pg-h2-spaced">Your money, legible — in Telegram and on the web.</h2>
        <div className="pg-features">
          {FEATURES.map((f) => (
            <div key={f.title} className="pg-reveal">
              <div className="pg-accent-bar" />
              <h3 className="pg-h3-sm">{f.title}</h3>
              <p className="pg-muted-sm">{f.copy}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="roadmap" className="pg-roadmap">
        <div className="pg-section">
          <span className="pg-reveal pg-kicker pg-kicker-light">What comes next</span>
          <h2 className="pg-reveal pg-h2 pg-h2-light">From tracking spends to building wealth.</h2>
          <p className="pg-reveal pg-roadmap-lead">
            PocketGuru starts with the money leaving your account. The plan is one place where young India learns to manage, save, invest and hit real goals.
          </p>
          <div className="pg-roadmap-grid">
            {ROADMAP.map((r) => (
              <div key={r.title} className="pg-reveal">
                <p className="pg-when">{r.when}</p>
                <h3 className="pg-h3-sm pg-h2-light">{r.title}</h3>
                <p className="pg-roadmap-copy">{r.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="privacy" className="pg-section pg-privacy">
        <div className="pg-reveal">
          <span className="pg-kicker">Privacy</span>
          <h2 className="pg-h2">We never ask for your bank password.</h2>
        </div>
        <div className="pg-reveal pg-trust-list">
          {TRUST.map((t) => (
            <p key={t} className="pg-trust-item">{t}</p>
          ))}
        </div>
      </section>

      <hr className="pg-rule" />

      <section id="start" className="pg-section pg-cta-band">
        <h2 className="pg-reveal pg-h2-xl">
          <span className="pg-block">Bank SMS in.</span>
          <span className="pg-block pg-accent">Clean expenses out.</span>
        </h2>
        <div className="pg-reveal pg-cta-row pg-cta-row-start">
          <Link to="/login" className="btn btn-primary pg-cta pg-cta-lg">Log in</Link>
          <div className="pg-register-wrap">
            <Link to="/register" className="btn btn-secondary pg-cta pg-cta-lg">Register</Link>
            <span className="pg-invite-note">Invite only for now</span>
          </div>
        </div>
      </section>

      <footer className="pg-footer">
        <span className="pg-footer-brand">Pocket<span>Guru</span></span>
        <span className="pg-footer-links">
          <a href="#how">How it works</a>
          <a href="#features">Features</a>
          <a href="#roadmap">Roadmap</a>
          <a href="#privacy">Privacy</a>
          <Link to="/login">Log in</Link>
        </span>
        <span>pocketguru.in</span>
      </footer>
    </div>
  );
}
